/**
 * ScoreBreakdownPanel Component
 * Phase 7: Displays detailed score calculation breakdown with explainability.
 */

import React from 'react'
import { ScoreBreakdown } from '../lib/api'
import { useLocale } from '../contexts/LocaleContext'

export interface ScoreBreakdownPanelProps {
  /** Breakdown data from backend */
  breakdown: ScoreBreakdown
  
  /** Score type label */
  scoreType: 'Risk' | 'Debt' | 'Compliance'
  
  /** Whether panel is initially expanded */
  initiallyExpanded?: boolean
}

const HOW_KEY: Record<ScoreBreakdownPanelProps['scoreType'], string> = {
  Risk: 'scores.howRisk',
  Debt: 'scores.howDebt',
  Compliance: 'scores.howCompliance',
}

export function ScoreBreakdownPanel({
  breakdown,
  scoreType,
  initiallyExpanded = false,
}: ScoreBreakdownPanelProps) {
  const { t } = useLocale()
  const [isExpanded, setIsExpanded] = React.useState(initiallyExpanded)
  const howLabel = t(HOW_KEY[scoreType])
  
  return (
    <div className="card">
      <button
        data-testid="expand-breakdown"
        className="w-full flex items-center justify-between text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={`breakdown-${scoreType.toLowerCase()}`}
      >
        <div>
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            {howLabel}
          </h3>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            {isExpanded ? t('scores.clickCollapse') : t('scores.clickExpand')}
          </p>
        </div>
        
        <svg
          className={`w-5 h-5 text-[var(--text-secondary)] transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      
      {isExpanded && (
        <div
          id={`breakdown-${scoreType.toLowerCase()}`}
          data-testid="score-breakdown-panel"
          className="mt-4 space-y-4 border-t border-[var(--border)] pt-4"
        >
          <div className="text-sm text-[var(--text-primary)] leading-relaxed">
            {breakdown.explanation}
          </div>
          
          <div className="grid gap-3 text-xs">
            <div className="flex justify-between items-center p-2 bg-[var(--surface-secondary)] rounded">
              <span className="text-[var(--text-secondary)]">
                {t('scores.weightedSum')}
              </span>
              <span className="font-mono text-[var(--text-primary)]">
                {breakdown.severity_weighted_sum.toFixed(2)}
              </span>
            </div>
            
            {breakdown.confidence_adjustment !== 1.0 && (
              <div className="flex justify-between items-center p-2 bg-[var(--surface-secondary)] rounded">
                <span className="text-[var(--text-secondary)]">
                  {t('scores.confAdj')}
                </span>
                <span className="font-mono text-[var(--text-primary)]">
                  ×{breakdown.confidence_adjustment.toFixed(2)}
                </span>
              </div>
            )}
            
            <div className="flex justify-between items-center p-2 bg-[var(--surface-secondary)] rounded">
              <span className="text-[var(--text-secondary)]">
                {t('scores.normFactor')}
              </span>
              <span className="font-mono text-[var(--text-primary)]">
                {breakdown.normalization_factor.toFixed(1)}
              </span>
            </div>
            
            <div className="flex justify-between items-center p-2 bg-[var(--accent)] bg-opacity-10 rounded border border-[var(--accent)]">
              <span className="text-[var(--text-primary)] font-medium">
                {t('scores.finalScore')}
              </span>
              <span className="font-mono text-[var(--accent)] font-medium">
                {breakdown.capped_value.toFixed(1)}
              </span>
            </div>
          </div>
          
          {breakdown.contributing_tracks.length > 0 && (
            <div className="pt-3 border-t border-[var(--border)]">
              <div className="text-xs text-[var(--text-secondary)] mb-2">
                {t('scores.contribTracks')}
              </div>
              <div className="flex flex-wrap gap-2">
                {breakdown.contributing_tracks.map((track) => (
                  <span
                    key={track}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium bg-[var(--surface-secondary)] text-[var(--text-primary)] rounded"
                  >
                    {track}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
